// Query — reads the current URL's query string.
//
// Every helper takes an optional `search` so callers can parse a string that
// isn't the live location (a link they're about to follow, a test fixture).
// Defaults to window.location.search, and returns empty results rather than
// throwing when there's no window at all.

function params(search?: string): URLSearchParams {
  if (search !== undefined) {
    return new URLSearchParams(search)
  }
  if (typeof window === "undefined") {
    return new URLSearchParams()
  }
  return new URLSearchParams(window.location.search)
}

export const Query = {
  // The raw value, or null when the parameter is absent. Note that a bare
  // "?debug" (no "=") yields "", not null — present but empty.
  get(name: string, search?: string): string | null {
    return params(search).get(name)
  },

  // Whether the parameter appears at all, regardless of its value.
  has(name: string, search?: string): boolean {
    return params(search).has(name)
  },

  // Case-insensitive, whitespace-tolerant string compare. `?debug=TRUE` and
  // `?debug=true%20` both match "true".
  is(name: string, expected: string, search?: string): boolean {
    const value = Query.get(name, search)
    if (value === null) return false
    return value.trim().toLowerCase() === expected.trim().toLowerCase()
  },

  // Truthy in the way a URL means it. Accepts the bare flag form (`?debug`)
  // alongside the spellings people actually type, and treats anything else —
  // including "false", "0", and "off" — as false. Deliberately not
  // "present means true": `?debug=false` reads as off to anyone looking at
  // the URL, and it should behave that way.
  flag(name: string, search?: string): boolean {
    const value = Query.get(name, search)
    if (value === null) return false
    const normalized = value.trim().toLowerCase()
    if (normalized === "") return true
    return normalized === "true" || normalized === "1" || normalized === "yes" || normalized === "on"
  },

  // All values for a repeated parameter: `?tag=a&tag=b` → ["a", "b"].
  all(name: string, search?: string): string[] {
    return params(search).getAll(name)
  },
}

// `?debug=true` — the escape hatch for turning on client-side logging in any
// environment, including production. Read once at module load: flipping it
// mid-session would give you a half-instrumented page, and the URL can't
// change without a navigation anyway.
export const DEBUG = Query.is("debug", "true")
